import { z } from "zod";

import { DocumentSourceSchema } from "@entities/document";

import { createComponentSchemas } from "../utils";
import {
  MenuSchema,
  RouteSchema,
  SocialLinkCollectionSchema,
} from "./collections.schema";
import {
  Button,
  ColorSchema,
  IconBaseSchema,
  IconsSchema,
} from "./common.schema";
import { StrapiMediaSchema } from "./media.schema";
import { SeoSchema } from "./seo.schema";

/* --- Music Track --- */
const MusicTrackBaseSchema = z.object({
  title: z.string().min(1),
  file: StrapiMediaSchema,
});

export const MusicTrack = createComponentSchemas(
  "ui.music-track",
  MusicTrackBaseSchema,
);

export type MusicTrackBase = z.infer<typeof MusicTrack.base>;

/* --- Weather --- */
const WeatherBaseSchema = z.object({
  temperature: z.number().int(),
  description: z.string(),
  icon: IconsSchema,
  useApi: z.boolean().optional().nullable(),
});

export const Weather = createComponentSchemas("ui.weather", WeatherBaseSchema);

export type WeatherBase = z.infer<typeof Weather.base>;

/* --- Header Menu Button --- */
export const HeaderMenuButtonSchema = z.object({
  id: z.number().int().positive().optional(),
  text: z.string(),
  link: z.string(),
  icon: IconBaseSchema.optional().nullable(),
  iconColor: ColorSchema.optional().nullable(),
});

/* --- Header --- */
export const HeaderSchema = z.object({
  id: z.number().int().positive().optional(),
  headerNav: MenuSchema.nullable(),
  menuButtons: z.array(HeaderMenuButtonSchema).max(4),
  phone: z.string().optional(),
  ctaButton: Button.base,
});

export type HeaderType = z.infer<typeof HeaderSchema>;

/* --- Footer Info --- */
export const FooterInfoSchema = z.object({
  id: z.number().int().positive(),
  address: z.string(),
  workingHours: z.string().optional(),
  partnersEmail: z.string().email(),
});

/* --- Footer Bottom --- */
export const FooterBottomSchema = z.object({
  id: z.number().int().positive(),
  copyright: z.string(),
  documents: z.array(DocumentSourceSchema).min(0).optional(),
});

/* --- Footer Links Column --- */
export const FooterLinksSchema = z.object({
  id: z.number().int().positive(),
  columnTitle: z.string(),
  routes: z.array(RouteSchema).min(0).optional(),
});

/* --- Footer --- */
export const FooterSchema = z.object({
  id: z.number().int().positive(),
  newsletterPersonalDataPolicy: DocumentSourceSchema.optional().nullable(),
  email: z.string().email(),
  phone: z.string(),
  footerNav: MenuSchema.nullable(),
  socialLinks: z.array(SocialLinkCollectionSchema).min(0).optional(),
  info: FooterInfoSchema,
  footerBottom: FooterBottomSchema,
});

export const AlertBannerSchema = z.object({
  text: z.string(),
  icon: IconBaseSchema,
  color: ColorSchema,
  isVisible: z.boolean(),
});

export type AlertBannerType = z.infer<typeof AlertBannerSchema>;

/* --- Global (Response) --- */
export const GlobalSchema = z.object({
  id: z.number().int().positive(),
  documentId: z.string(),
  feedbackEmail: z.string().email(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  publishedAt: z.string().datetime().optional(),
  header: HeaderSchema,
  footer: FooterSchema,
  ticketWidgetSettings: z.any().optional().nullable(),
  feedbackForm: z
    .object({
      title: z.string(),
      description: z.string(),
      feedbackEmail: z.string().email(),
      privacyPolicy: DocumentSourceSchema.optional().nullable(),
      privacyPolicyAlt: DocumentSourceSchema.optional().nullable(),
    })
    .optional()
    .nullable(),
  seo: SeoSchema.optional().nullable(),
  musicTrack: MusicTrack.base.optional().nullable(),
  weather: Weather.base.optional().nullable(),
  alertBanner: AlertBannerSchema.optional().nullable(),
});

export const GlobalDtoSchema = z.object({
  data: GlobalSchema,
});

export type GlobalType = z.infer<typeof GlobalSchema>;
